import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { defaultHeaders } from '@redux/api/Config';
import {
  MarkType,
  ModelType,
  ModificationType,
  RecommendedDiametersType,
  RecommendedSizesType,
  CarBodiesType,
  CarBatterySpecification,
} from '../types/autoSelectsTypes';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import config from '@app/config';

function isHydrateAction(action: Action): action is PayloadAction<any> {
  return action.type === HYDRATE;
}

export const autoApi = createApi({
  reducerPath: 'autoApi',
  baseQuery: fetchBaseQuery({
    baseUrl: config.baseApiUrl,
    prepareHeaders: defaultHeaders,
  }),
  extractRehydrationInfo(action, { reducerPath }): any {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath];
    }
  },
  endpoints: builder => ({
    getListMark: builder.query<MarkType[], void>({
      query: () => '/api/catalog/v1/get-auto-mark',
      transformResponse: (response: { data }): MarkType[] => response.data,
    }),
    getListModel: builder.query<ModelType[], number>({
      query: markId => `/api/catalog/v1/get-auto-model?markId=${markId}`,
      transformResponse: (response: { data }): ModelType[] => response.data,
    }),
    getListYear: builder.query<string[], { groupCode: string; markId: number }>({
      query: ({ groupCode, markId }) => `/api/catalog/v1/get-auto-year?groupCode=${groupCode}&markId=${markId}`,
      transformResponse: (response: { data: number[] }): string[] => response.data.map(x => x.toString()),
    }),
    getListModification: builder.query<ModificationType[], { groupCode: string; markId: number; year: string }>({
      query: query => {
        return `/api/catalog/v1/get-auto-modification?groupCode=${query.groupCode}&markId=${query.markId}&year=${query.year}`;
      },
      transformResponse: (response: { data }): ModificationType[] => response.data,
    }),
    getListCarBodies: builder.query<CarBodiesType[], { groupCode: string; markId: number; year?: number }>({
      query: ({ markId, groupCode, year }) => {
        return `/api/catalog/get-auto-bodies?markId=${markId}&groupCode=${groupCode}${year ? `&year=${year}` : ''}`;
      },
      transformResponse: (response: { data }): CarBodiesType[] => response.data,
    }),
    getListCarEngineModifications: builder.query<
      ModificationType[],
      { groupCode: string; markId: number; carBody: string; yearStart: number; yearEnd: number }
    >({
      query: ({ markId, groupCode, carBody, yearStart, yearEnd }) => {
        return (
          '/api/catalog/get-auto-engine-modifications' +
          `?markId=${markId}&groupCode=${groupCode}&carBody=${carBody}&yearStart=${yearStart}&yearEnd=${yearEnd}`
        );
      },
      transformResponse: (response: { data }): ModificationType[] => response.data,
    }),
    getListDiameter: builder.query<RecommendedDiametersType, { markId: number; groupCode: string; year?: string }>({
      query: query => {
        return `/api/catalog/get-recommended-diameters-car?markId=${query.markId}&groupCode=${query.groupCode}${query.year ? `&year=${query.year}` : ''}`;
      },
      transformResponse: (response: { data }): RecommendedDiametersType => response.data,
    }),
    getListSizes: builder.query<RecommendedSizesType, { modificationId: number }>({
      query: query => {
        return `/api/catalog/get-auto-recommended-sizes?modificationId=${query.modificationId}`;
      },
      transformResponse: (response: { data }): RecommendedSizesType => response.data,
    }),
    getCarBatterySpecifications: builder.query<CarBatterySpecification[], { modificationId: number }>({
      query: query => {
        return `/api/catalog/get-car-battery-specifications?modificationId=${query.modificationId}`;
      },
      transformResponse: (response: { data }): CarBatterySpecification[] =>
        response.data.map(specification => ({
          width: specification.size.width,
          height: specification.size.height,
          length: specification.size.length,
          continent: specification.continent,
          capacity: specification.capacity,
          terminalType: specification.terminalType,
          terminalLocation: specification.terminalLocation,
          polarity: specification.polarity,
        })),
    }),
    hasSensor: builder.query<boolean, { modificationId: number }>({
      query: query => {
        return `/api/catalog/has-sensor?modificationId=${query.modificationId}`;
      },
      transformResponse: (response: { data }): boolean => response.data,
    }),
  }),
});

export const {
  useGetListMarkQuery,
  useGetListModelQuery,
  useGetListYearQuery,
  useGetListModificationQuery,
  useGetListDiameterQuery,
  useGetListSizesQuery,
  useGetCarBatterySpecificationsQuery,
  useGetListCarBodiesQuery,
  useGetListCarEngineModificationsQuery,
  useHasSensorQuery,
  endpoints: autoEndpoints,
} = autoApi;
